import AppNumber from "@components/number";
import TimeSince from "@components/timeSince";
import { knolToFSRSCard } from "@core/fsrs/scheduler";
import { IonItem, IonLabel, IonList, IonListHeader, IonNote } from "@ionic/react";
import { IKnol } from "@models/knol";
import L10n from "localization";

interface IProps {
  knol?: IKnol;
}

/**
 * FSRS Info Component
 *
 * Displays FSRS (Free Spaced Repetition Scheduler) specific details for a card.
 * Shows state, due date, stability, difficulty, and review history.
 *
 * Usage:
 * ```tsx
 * <FSRSInfo knol={knol} />
 * ```
 *
 * Only displays when the deck is in FSRS mode and the knol has FSRS data.
 * Returns null if no FSRS data is available.
 */

export default function FSRSInfo({ knol }: IProps) {
  if (!knol) {
    return null;
  }

  const fsrsCard = knolToFSRSCard(knol);

  if (!fsrsCard) {
    return null;
  }

  const now = Date.now();
  const isDue = fsrsCard.due <= now;

  // Calculate intervals for display
  const timeDiff = fsrsCard.due - now;
  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

  // Format state for display
  const stateDisplayNames = {
    New: L10n.localize((s) => s.fsrs.stateNew),
    Learning: L10n.localize((s) => s.fsrs.stateLearning),
    Review: L10n.localize((s) => s.fsrs.stateReview),
    Relearning: L10n.localize((s) => s.fsrs.stateRelearning),
  };

  const stateDisplay = stateDisplayNames[fsrsCard.state] || fsrsCard.state;

  return (
    <IonList>
      <IonListHeader>{L10n.localize((s) => s.fsrs.title)}</IonListHeader>

      <IonItem>
        <IonLabel>
          <h2>{L10n.localize((s) => s.fsrs.stateTitle)}</h2>
          <p>{L10n.localize((s) => s.fsrs.stateDescription)}</p>
        </IonLabel>
        <IonNote slot="end">{stateDisplay}</IonNote>
      </IonItem>

      {(isDue || fsrsCard.state === "New") && (
        <IonItem>
          <IonLabel>
            <h2>{L10n.localize((s) => s.fsrs.dueTitle)}</h2>
            <p>{L10n.localize((s) => s.fsrs.dueDescription)}</p>
          </IonLabel>
          <IonNote slot="end" color={isDue ? "danger" : "medium"}>
            {fsrsCard.state === "New"
              ? L10n.localize((s) => s.fsrs.dueNow)
              : L10n.localize((s) => s.fsrs.dueNow)}
          </IonNote>
        </IonItem>
      )}

      <IonItem>
        <IonLabel>
          <h2>{L10n.localize((s) => s.fsrs.stabilityTitle)}</h2>
          <p>{L10n.localize((s) => s.fsrs.stabilityDescription)}</p>
        </IonLabel>
        <IonNote slot="end">
          {fsrsCard.state === "New" ? (
            "—"
          ) : (
            <>
              <AppNumber num={Math.round(fsrsCard.stability * 10) / 10} /> days
            </>
          )}
        </IonNote>
      </IonItem>

      <IonItem>
        <IonLabel>
          <h2>{L10n.localize((s) => s.fsrs.difficultyTitle)}</h2>
          <p>{L10n.localize((s) => s.fsrs.difficultyDescription)}</p>
        </IonLabel>
        <IonNote slot="end">
          {fsrsCard.state === "New" ? (
            "—"
          ) : (
            <AppNumber num={Math.round(fsrsCard.difficulty * 100) / 100} />
          )}
        </IonNote>
      </IonItem>

      {fsrsCard.lastReview && fsrsCard.state !== "New" && (
        <IonItem>
          <IonLabel>
            <h2>{L10n.localize((s) => s.fsrs.lastReviewTitle)}</h2>
            <p>{L10n.localize((s) => s.fsrs.lastReviewDescription)}</p>
          </IonLabel>
          <IonNote slot="end">
            <TimeSince time={new Date(fsrsCard.lastReview)} />
          </IonNote>
        </IonItem>
      )}

      {!isDue && fsrsCard.state !== "New" && daysDiff > 0 && (
        <IonItem>
          <IonLabel>
            <h2>{L10n.localize((s) => s.fsrs.nextReviewTitle)}</h2>
            <p>{L10n.localize((s) => s.fsrs.nextReviewDescription)}</p>
          </IonLabel>
          <IonNote slot="end">
            {daysDiff === 1 ? L10n.localize((s) => s.fsrs.nextReviewTomorrow) : `${daysDiff} days`}
          </IonNote>
        </IonItem>
      )}
    </IonList>
  );
}
